import React, { useEffect, useState } from "react";
import {
  isWebAuthnSupported,
  prepareCreateOptions,
  prepareGetOptions,
  serializeCreateCredential,
  serializeGetCredential,
} from "@/lib/webauthn";
import {
  verifyPin,
  getRegisterOptions,
  finishRegister,
  getAuthOptions,
  finishAuth,
  saveSession,
  LICENSE_ID_KEY,
} from "@/lib/authApi";
import { APP_VERSION } from "@/lib/version";
import Signup from "./Signup";

// Matches the approved mockup at /login-final-approved.html — cream ground,
// Fraunces serif wordmark top-left, v1.0 pill top-right, symmetric two-line
// headline, primary "Continue with Face ID" button, PIN entry as fallback.
export default function Login({ onAuthenticated }) {
  // If we returned from Stripe with ?signup_session=..., jump directly into
  // the Signup flow so the poller can show the newly-minted license PIN.
  const returningFromStripe = (() => {
    if (typeof window === "undefined") return false;
    try {
      return new URLSearchParams(window.location.search).has("signup_session");
    } catch {
      return false;
    }
  })();
  const [mode, setMode] = useState(returningFromStripe ? "signup" : "home"); // "home" | "pin" | "signup"
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [inIframe, setInIframe] = useState(false);

  // If the device already has a paired license, jump straight to Face ID.
  useEffect(() => {
    if (!isWebAuthnSupported()) {
      setError("This browser doesn't support Face ID. Update iOS/Safari or use a modern browser.");
    }
    // WebAuthn refuses to run in a cross-origin iframe (Emergent's preview chrome
    // renders us inside one). Detect that and route the user to open the app in
    // its own Safari tab / installed PWA.
    try {
      if (typeof window !== "undefined" && window.self !== window.top) {
        setInIframe(true);
      }
    } catch {
      // Access to window.top can itself throw when cross-origin — that's the
      // strongest signal we're framed.
      setInIframe(true);
    }
  }, []);

  // Paint the html + body cream while this screen is mounted so iOS Safari's
  // rubber-band overscroll shows cream instead of the default light-gray body
  // background. Overscroll-behavior alone is enough to lock the bounce — we do
  // NOT set position:fixed on body here because that leaves the layout shifted
  // when the iOS software keyboard opens then closes (which is why the top
  // wordmark drifted up after tapping Back from the PIN screen).
  //
  // NOTE: The global cream background is set inline in index.html and again in
  // index.css so this useEffect intentionally does nothing at mount time —
  // kept as a placeholder in case a future variant needs different backgrounds.
  // Login screen background is handled entirely by the global cream styles in
  // index.html (inline) and index.css. No per-mount body-style overrides here,
  // because the previous approach caused a one-frame repaint on mount that
  // showed up as a visible "flash" during PWA launch. The pa-auth-root element
  // itself paints cream (position:fixed;inset:0), which is enough.

  function openInNewTab() {
    try {
      window.open(window.location.href, "_blank", "noopener,noreferrer");
    } catch {
      // no-op — user can long-press the link if this fails
    }
  }

  async function handleFaceIdLogin() {
    setBusy(true);
    setError("");
    try {
      const savedLicense = localStorage.getItem(LICENSE_ID_KEY);
      // Always attempt WebAuthn. If we don't have a saved license_id, the
      // backend returns a "discoverable credential" challenge and the browser
      // lets the user pick from any resident passkey scoped to this host.
      const optsResp = await getAuthOptions(savedLicense || null);
      const { publicKey, challenge_token } = optsResp;
      const cred = await navigator.credentials.get({
        publicKey: prepareGetOptions(publicKey),
      });
      const serialized = serializeGetCredential(cred);
      const result = await finishAuth({
        license_id: savedLicense || undefined,
        credential: serialized,
        challenge_token,
      });
      saveSession({
        session_token: result.session_token,
        license_id: result.license_id || savedLicense,
        pharmacy_name: result.pharmacy_name,
      });
      onAuthenticated?.(result);
    } catch (e) {
      const msg = String(e.message || e);
      const name = String(e.name || "");
      const lower = msg.toLowerCase();
      const isNotAllowed =
        name === "NotAllowedError" ||
        lower.includes("not allowed") ||
        lower.includes("denied permission") ||
        lower.includes("cancel");
      if (msg.includes("NO_PASSKEY") || msg.includes("LICENSE_NOT_FOUND")) {
        setMode("pin");
        setError("");
      } else if (isNotAllowed) {
        setError("Face ID was cancelled or not available.\nTap Continue with Face ID to try again.");
      } else {
        setError(humanError(msg));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handlePinSubmit(e) {
    e.preventDefault();
    if (!pin.trim()) return;
    setBusy(true);
    setError("");
    try {
      const verify = await verifyPin(pin.trim());
      const { publicKey } = await getRegisterOptions(verify.registration_token);
      const cred = await navigator.credentials.create({
        publicKey: prepareCreateOptions(publicKey),
      });
      const serialized = serializeCreateCredential(cred);
      const finished = await finishRegister(verify.registration_token, serialized);
      saveSession({
        session_token: finished.session_token,
        license_id: verify.license_id,
        pharmacy_name: finished.pharmacy_name,
      });
      onAuthenticated?.(finished);
    } catch (e) {
      setError(humanError(String(e.message || e)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pa-auth-root">
      <style>{styles}</style>
      {mode === "signup" ? (
        <Signup
          onCancel={() => setMode("home")}
          onCompleted={(payload) => {
            // Signup completed with a fresh PIN. Drop the user on the PIN
            // screen with the newly-issued PIN pre-filled so they can pair
            // this device with a single tap.
            setPin(payload?.pin || "");
            setMode("pin");
          }}
        />
      ) : (
        <>
      <div className="pa-topbar">
        <div className="pa-brand" data-testid="brand-wordmark">
          Pharmacy <em>Analyzer</em>
        </div>
        <div className="pa-top-actions">
          <button
            type="button"
            className="pa-signup-pill"
            onClick={() => setMode("signup")}
            data-testid="signup-pill-btn"
          >
            Sign up
          </button>
          <span className="pa-ver">{APP_VERSION}</span>
        </div>
      </div>

      <div className="pa-body">
        {inIframe ? (
          <div className="pa-iframe-warn" data-testid="iframe-warning">
            <div className="pa-headline">Open in <em>Safari</em><br />to pair this device</div>
            <div className="pa-subhead">
              Face ID needs to run in Safari directly (not inside a preview frame).
              Tap the button below, or open this URL in a new Safari tab and add it
              to your Home Screen for the best experience.
            </div>
            <button
              type="button"
              className="pa-primary"
              onClick={openInNewTab}
              data-testid="open-in-safari-btn"
            >
              Open in Safari →
            </button>
            <div className="pa-hint">
              Or copy this address and paste it into Safari:<br />
              <code className="pa-url">{typeof window !== "undefined" ? window.location.href : ""}</code>
            </div>
          </div>
        ) : mode === "home" ? (
          <>
            <div className="pa-headline" data-testid="login-headline">
              Login to upload<br />your <em>pharmacy data</em>
            </div>
            <div className="pa-subhead">
              Know exactly how your pharmacy<br />is performing — anytime, at your fingertips.
            </div>
            <button
              type="button"
              className="pa-primary"
              onClick={handleFaceIdLogin}
              disabled={busy}
              data-testid="continue-face-id-btn"
            >
              {busy ? "…" : (<><FaceIcon /> Continue with Face ID</>)}
            </button>
            <button
              type="button"
              className="pa-alt"
              onClick={() => setMode("pin")}
              disabled={busy}
              data-testid="use-pin-btn"
            >
              Have a PIN? Enter it →
            </button>
            <button
              type="button"
              className="pa-alt pa-alt-muted"
              onClick={() => setMode("recover")}
              disabled={busy}
              data-testid="recover-pin-btn"
            >
              Already paid but no PIN? Email it to me →
            </button>
            {error ? <div className="pa-error" data-testid="login-error">{error}</div> : null}
          </>
        ) : mode === "recover" ? (
          <RecoverPin onBack={() => setMode("home")} />
        ) : (
          <form onSubmit={handlePinSubmit} className="pa-form">
            <div className="pa-headline">Enter your <em>PIN</em></div>
            <div className="pa-subhead">
              First-time setup. Paste the PIN we sent you<br />to pair this device with your license.
            </div>
            <input
              type="text"
              value={pin}
              onChange={(e) => setPin(e.target.value.toUpperCase())}
              placeholder="PH-XXXX-XXXX"
              autoFocus
              autoCorrect="off"
              autoCapitalize="characters"
              spellCheck="false"
              className="pa-input"
              disabled={busy}
              data-testid="pin-input"
            />
            <button
              type="submit"
              className="pa-primary"
              disabled={busy || !pin.trim()}
              data-testid="pin-submit-btn"
            >
              {busy ? "Verifying…" : "Verify & pair this device"}
            </button>
            <button
              type="button"
              className="pa-alt"
              onClick={() => {
                // Blur the input so the iOS keyboard closes before the mode
                // swap animates. The auth root is position:fixed, so there is
                // no scroll offset to restore.
                if (typeof document !== "undefined" && document.activeElement && typeof document.activeElement.blur === "function") {
                  document.activeElement.blur();
                }
                setMode("home");
                setError("");
                setPin("");
              }}
              disabled={busy}
              data-testid="pin-back-btn"
            >
              ← Back
            </button>
            {error ? <div className="pa-error" data-testid="login-error">{error}</div> : null}
          </form>
        )}
      </div>

      <div className="pa-foot">
        <strong>Processed in-memory.</strong><br />
        Your data is never stored on our servers.
      </div>
        </>
      )}
    </div>
  );
}

function FaceIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 8V6a2 2 0 0 1 2-2h2" />
      <path d="M16 4h2a2 2 0 0 1 2 2v2" />
      <path d="M20 16v2a2 2 0 0 1-2 2h-2" />
      <path d="M8 20H6a2 2 0 0 1-2-2v-2" />
      <circle cx="9" cy="10" r="0.9" fill="currentColor" />
      <circle cx="15" cy="10" r="0.9" fill="currentColor" />
      <path d="M9.5 15c.83.66 1.83 1 3 1s2.17-.34 3-1" />
    </svg>
  );
}

function RecoverPin({ onBack }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/auth/signup/recover`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const text = await res.text();
      let json;
      try { json = text ? JSON.parse(text) : {}; } catch { json = {}; }
      if (!res.ok) {
        throw new Error(String(json.detail || `HTTP_${res.status}`));
      }
      setSent(true);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div data-testid="recover-pin-sent">
        <div className="pa-headline">Check your <em>inbox</em></div>
        <div className="pa-subhead">
          If <strong>{email}</strong> is on file, we&rsquo;ve sent your
          license PIN to that address.<br />
          It should arrive within a few minutes.<br /><br />
          <span style={{ fontSize: "11px", color: "#7A6947" }}>
            Not seeing it? Check your spam folder or contact support.
          </span>
        </div>
        <button
          type="button"
          className="pa-primary"
          onClick={onBack}
          data-testid="recover-pin-done-btn"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="pa-form" data-testid="recover-pin-form">
      <div className="pa-headline">Recover your <em>PIN</em></div>
      <div className="pa-subhead">
        Enter the email you used at checkout.<br />
        We&rsquo;ll send your PIN to the address on file.
      </div>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@yourpharmacy.ca"
        autoFocus
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck="false"
        className="pa-input"
        disabled={busy}
        data-testid="recover-email-input"
        style={{ textAlign: "left", letterSpacing: 0, textTransform: "none" }}
      />
      <button
        type="submit"
        className="pa-primary"
        disabled={busy || !email.trim()}
        data-testid="recover-submit-btn"
      >
        {busy ? "Sending…" : "Email me my PIN"}
      </button>
      <button
        type="button"
        className="pa-alt"
        onClick={onBack}
        disabled={busy}
        data-testid="recover-back-btn"
      >
        ← Back
      </button>
      {error ? <div className="pa-error" data-testid="recover-error">{error}</div> : null}
    </form>
  );
}

function humanError(msg) {
  const lower = String(msg || "").toLowerCase();
  if (msg.includes("INVALID_PIN")) return "That PIN doesn't match any active license.";
  if (msg.includes("PIN_ALREADY_USED")) return "This PIN was already used.\nContact support for a new one.";
  if (msg.includes("ALREADY_PAIRED")) return "This license already has a passkey on another device.";
  if (msg.includes("CREDENTIAL_MISMATCH")) return "Face ID verification failed.\nTap Continue with Face ID to try again.";
  if (msg.includes("NO_PASSKEY_REGISTERED")) return "No passkey on this device.\nEnter your PIN to pair it.";
  if (lower.includes("network")) return "Can't reach the server.\nCheck your connection and try again.";
  if (lower.includes("origin of the document")) {
    return "Face ID can't run in a preview frame.\nTap \"Open in Safari\" above to continue.";
  }
  // Catch-all for any raw WebAuthn/browser error text so users never see the
  // technical DOMException string.
  if (lower.includes("not allowed") || lower.includes("denied permission") ||
      lower.includes("user agent") || lower.includes("timeout") ||
      lower.includes("rpid") || lower.includes("credential")) {
    return "Face ID didn't complete.\nTap Continue with Face ID to try again.";
  }
  return "Something went wrong.\nTap Continue with Face ID to try again.";
}

const styles = `
/* Fonts (Fraunces, Geist, Inter, Space Grotesk) are preloaded in index.html
   so they're ready before this component mounts — no @import here to avoid
   a font-swap flash on PWA launch. */

.pa-auth-root {
  /* Anchored to the full visual viewport with position:fixed so the top
     wordmark and bottom footer stay locked in place — even after the iOS
     software keyboard opens and closes. inset:0 stretches to top/right/
     bottom/left of the viewport (respecting viewport-fit=cover). */
  position: fixed;
  inset: 0;
  background: #F7F3EC;
  color: #1B3B2E;
  font-family: 'Geist', 'Inter', -apple-system, sans-serif;
  display: flex;
  flex-direction: column;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
}
.pa-topbar {
  /* Use max() with a 52px floor so the top wordmark ALWAYS clears the iPhone
     status bar, even in Safari contexts where env(safe-area-inset-top) reports
     0 (which happens intermittently after the software keyboard closes). */
  padding-top: max(52px, calc(26px + env(safe-area-inset-top)));
  padding-left: 24px;
  padding-right: 24px;
  display: flex; align-items: center; justify-content: space-between;
}
.pa-brand {
  font-family: 'Fraunces', serif;
  font-size: 17px;
  font-weight: 400;
  letter-spacing: -0.015em;
  color: #1B3B2E;
}
.pa-brand em { font-style: italic; color: #2B5E4A; font-weight: 400; }
.pa-ver {
  font-family: 'Geist','Inter'; font-size: 10px; color: #9B8666;
  background: rgba(93,74,40,0.08); padding: 3px 9px; border-radius: 100px;
  font-weight: 500;
}
.pa-top-actions { display: inline-flex; align-items: center; gap: 8px; }
/* Sign-up pill sits inline with the version pill. Exactly the same
   padding + font-size as .pa-ver (per user request "keep the exact same
   size as the version or the logout") — differentiation is by color only:
   dark-green fill so it reads as the primary CTA on the login screen. */
.pa-signup-pill {
  font-family: 'Geist','Inter';
  font-size: 10px;
  padding: 3px 9px;
  border-radius: 100px;
  font-weight: 600;
  background: #1B3B2E;
  color: #FBF6E9;
  border: none;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition: opacity 0.15s, transform 0.05s;
}
.pa-signup-pill:hover:not(:disabled) { opacity: 0.9; }
.pa-signup-pill:active:not(:disabled) { transform: scale(0.96); }
.pa-body {
  flex: 1;
  padding: 60px 30px 24px;
  display: flex; flex-direction: column; justify-content: center;
  text-align: center;
  max-width: 460px;
  margin: 0 auto;
  width: 100%;
}
.pa-form { display: contents; }
.pa-headline {
  font-family: 'Fraunces', serif;
  font-size: 28px;
  font-weight: 400;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: #1B3B2E;
  margin-bottom: 16px;
}
.pa-headline em { font-style: italic; color: #2B5E4A; font-weight: 400; }
.pa-subhead {
  font-size: 13px;
  color: #5D4A28;
  line-height: 1.55;
  max-width: 300px;
  margin: 0 auto 30px;
}
.pa-input {
  width: 100%;
  max-width: 320px;
  margin: 0 auto 12px;
  padding: 14px 16px;
  border: 1.5px solid rgba(27,59,46,0.15);
  border-radius: 12px;
  background: white;
  font-family: 'Geist','Inter';
  /* IMPORTANT: iOS Safari auto-zooms the whole page when the user taps
     an input with font-size < 16px (and often refuses to zoom back).
     Do NOT lower. */
  font-size: 16px;
  color: #1B3B2E;
  letter-spacing: 0.05em;
  text-align: center;
  outline: none;
}
.pa-input:focus { border-color: #1B3B2E; }
.pa-primary {
  background: #1B3B2E;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  color: white;
  font-family: 'Geist','Inter';
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0 auto 10px;
  cursor: pointer;
  max-width: 320px;
  width: 100%;
  transition: opacity 0.15s, transform 0.05s;
}
.pa-primary:hover:not(:disabled) { opacity: 0.9; }
.pa-primary:active:not(:disabled) { transform: scale(0.98); }
.pa-primary:disabled { opacity: 0.5; cursor: default; }
.pa-alt {
  background: transparent;
  border: none;
  color: #1B3B2E;
  font-family: 'Geist','Inter';
  font-size: 12.5px;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-thickness: 1px;
  text-decoration-color: rgba(27,59,46,0.3);
  padding: 11px;
  cursor: pointer;
}
.pa-alt:disabled { opacity: 0.4; cursor: default; }
.pa-alt-muted {
  color: #7A6947;
  text-decoration-color: rgba(122,105,71,0.35);
  font-weight: 400;
}
.rec-pin-box {
  border: 1px dashed rgba(43,94,74,0.35);
  background: rgba(43,94,74,0.05);
  border-radius: 14px;
  padding: 14px 18px;
  margin: 14px auto 16px;
  max-width: 320px;
}
.rec-pin-label {
  font-size: 10px;
  color: #5D4A28;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-bottom: 4px;
}
.rec-pin-value {
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 20px;
  color: #1B3B2E;
  letter-spacing: 0.12em;
  font-weight: 600;
  margin-bottom: 8px;
  user-select: all;
  -webkit-user-select: all;
}
.rec-copy-btn {
  background: transparent;
  border: 1px solid rgba(27,59,46,0.22);
  color: #1B3B2E;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 100px;
  cursor: pointer;
}
.rec-copy-btn:hover { background: rgba(27,59,46,0.06); }
.pa-error {
  color: #862F1A;
  background: #FDECE7;
  border: 1px solid #E9AC9C;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 12px;
  margin-top: 16px;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.4;
  white-space: pre-line;
}
.pa-foot {
  padding: 14px 30px calc(24px + env(safe-area-inset-bottom));
  text-align: center;
  font-size: 10.5px;
  color: #7A6947;
  line-height: 1.5;
}
.pa-foot strong {
  color: #1B3B2E;
  font-weight: 600;
  font-family: 'Fraunces';
  font-style: italic;
  font-size: 11.5px;
}
.pa-iframe-warn { display: contents; }
.pa-hint {
  font-size: 11px;
  color: #7A6947;
  line-height: 1.55;
  margin-top: 22px;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}
.pa-url {
  display: inline-block;
  margin-top: 6px;
  padding: 6px 10px;
  background: rgba(27,59,46,0.06);
  border-radius: 8px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10.5px;
  color: #1B3B2E;
  word-break: break-all;
  user-select: all;
  -webkit-user-select: all;
}
`;
